import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { getConsent, saveConsent } from "@/lib/consent";
import { useI18n } from "@/context/I18nContext";

const TX = {
  de: {
    title: "Cookies & Datenschutz",
    text: "Technisch notwendige Cookies (Anmeldung, Sprache, diese Auswahl) sind immer aktiv. Analyse- und Marketing-Cookies setzen wir nur mit Ihrer Zustimmung. Sie können Ihre Wahl jederzeit im Footer unter „Cookie-Einstellungen“ ändern.",
    necessary: "Notwendig", necessary_hint: "immer aktiv",
    analytics: "Analyse", analytics_hint: "anonyme Nutzungsstatistik zur Verbesserung der App",
    marketing: "Marketing", marketing_hint: "derzeit nicht im Einsatz",
    all: "Alle akzeptieren", none: "Nur notwendige", save: "Auswahl speichern", more: "Datenschutzerklärung",
  },
  en: {
    title: "Cookies & privacy",
    text: "Strictly necessary cookies (login, language, this choice) are always on. Analytics and marketing cookies are only set with your consent. You can change your choice anytime via “Cookie settings” in the footer.",
    necessary: "Necessary", necessary_hint: "always on",
    analytics: "Analytics", analytics_hint: "anonymous usage statistics to improve the app",
    marketing: "Marketing", marketing_hint: "currently not used",
    all: "Accept all", none: "Necessary only", save: "Save selection", more: "Privacy policy",
  },
};

function Row({ label, hint, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} />
    </div>
  );
}

export default function CookieConsent() {
  const { lang } = useI18n();
  const tx = TX[lang] || TX.de;
  const [open, setOpen] = useState(() => !getConsent());
  const [sel, setSel] = useState(() => getConsent() || { analytics: false, marketing: false });

  useEffect(() => {
    const onOpen = () => {
      setSel(getConsent() || { analytics: false, marketing: false });
      setOpen(true);
    };
    window.addEventListener("wp-open-consent", onOpen);
    return () => window.removeEventListener("wp-open-consent", onOpen);
  }, []);

  if (!open) return null;
  const done = (choice) => {
    saveConsent(choice);
    setOpen(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-5" data-testid="cookie-banner">
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-background/95 p-5 shadow-2xl backdrop-blur-md">
        <div className="mb-2 flex items-center gap-2">
          <Cookie className="h-5 w-5 text-primary" />
          <h2 className="text-base font-bold">{tx.title}</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          {tx.text}{" "}
          <Link to="/datenschutz" className="underline" onClick={() => setOpen(false)} data-testid="cookie-privacy-link">{tx.more}</Link>
        </p>
        <div className="my-3 divide-y divide-border rounded-md border border-border px-3">
          <Row label={tx.necessary} hint={tx.necessary_hint} checked disabled testId="cookie-necessary-switch" />
          <Row label={tx.analytics} hint={tx.analytics_hint} checked={!!sel.analytics}
            onChange={(v) => setSel((s) => ({ ...s, analytics: v }))} testId="cookie-analytics-switch" />
          <Row label={tx.marketing} hint={tx.marketing_hint} checked={!!sel.marketing}
            onChange={(v) => setSel((s) => ({ ...s, marketing: v }))} testId="cookie-marketing-switch" />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => done({ analytics: false, marketing: false })} data-testid="cookie-reject-btn">{tx.none}</Button>
          <Button variant="outline" onClick={() => done(sel)} data-testid="cookie-save-btn">{tx.save}</Button>
          <Button onClick={() => done({ analytics: true, marketing: true })} data-testid="cookie-accept-all-btn">{tx.all}</Button>
        </div>
      </div>
    </div>
  );
}
